import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  AlignCenter,
  AlignLeft,
  ArrowLeft,
  Check,
  Clock,
  Edit3,
  FileText,
  Image,
  Link,
  Lock,
  LogOut,
  MoreHorizontal,
  Maximize2,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import {
  createIntelligenceItem,
  deleteIntelligenceItem,
  fetchAdminIntelligence,
  getAdminSession,
  itemToFormData,
  signInAdmin,
  signOutAdmin,
  updateIntelligenceItem,
} from '../services/intelligenceService';

const CATEGORIES = ['PERSPECTIVE', 'PODCAST', 'VIDEO', 'NEWS'];
const MEDIA_TYPES = ['article', 'podcast', 'video'];
const STORY_TABS = [
  { id: 'drafts', label: 'Drafts' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'published', label: 'Published' },
  { id: 'unlisted', label: 'Unlisted' },
  { id: 'submissions', label: 'Submissions', separated: true },
];
const BLOCK_COMMANDS = [
  { id: 'image', label: 'Image', description: 'Upload an image', icon: Image },
  { id: 'h2', label: 'Heading', description: 'Section heading', icon: FileText },
  { id: 'quote', label: 'Quote', description: 'Emphasized passage', icon: MoreHorizontal },
  { id: 'divider', label: 'Divider', description: 'Separate sections', icon: MoreHorizontal },
  { id: 'code', label: 'Code block', description: 'Formatted code', icon: FileText },
  { id: 'callout', label: 'Callout', description: 'Highlighted note', icon: AlertCircle },
  { id: 'bullets', label: 'Bullet list', description: 'Unordered list', icon: MoreHorizontal },
  { id: 'numbered', label: 'Numbered list', description: 'Ordered list', icon: MoreHorizontal },
];
const IMAGE_SIZE_OPTIONS = [
  { id: 'compact', label: 'Small image', icon: AlignLeft },
  { id: 'regular', label: 'Medium image', icon: AlignCenter },
  { id: 'full', label: 'Full width image', icon: Maximize2 },
];
const AUTOSAVE_DELAY_MS = 700;
const MAX_INLINE_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_EDITOR_TAGS = new Set([
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CODE',
  'DIV',
  'EM',
  'FIGURE',
  'H1',
  'H2',
  'H3',
  'HR',
  'I',
  'IMG',
  'LI',
  'OL',
  'P',
  'PRE',
  'STRONG',
  'U',
  'UL',
]);

const getFreshForm = () => ({
  databaseId: null,
  title: '',
  slug: '',
  subtitle: '',
  author: 'Panometra Research',
  category: 'PERSPECTIVE',
  mediaType: 'article',
  imageUrl: '',
  mediaUrl: '',
  sourceUrl: '',
  readTime: '',
  publishedAt: new Date().toISOString().slice(0, 10),
  isPublished: false,
  body: '',
});

const createSlugPreview = (value) => (
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
);

const estimateReadTime = (body) => {
  const words = body.trim().split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.ceil(words / 220))} min read`;
};

const formatUpdatedDate = (item) => {
  const dateValue = item.updated_at || item.created_at || item.published_at;
  if (!dateValue) return 'Updated today';

  return `Updated ${new Date(dateValue).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: new Date(dateValue).getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  })}`;
};

const countWords = (body = '') => body.trim().split(/\s+/).filter(Boolean).length;

const escapeHtml = (value = '') => (
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
);

const isSafeEditorUrl = (value = '') => {
  const url = String(value).trim();
  return /^(https?:|mailto:|data:image\/)/i.test(url) || url.startsWith('/') || url.startsWith('#');
};

const sanitizeEditorNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent || '');
  if (node.nodeType !== Node.ELEMENT_NODE) return document.createTextNode('');

  const tagName = node.tagName.toUpperCase();
  if (['IFRAME', 'OBJECT', 'SCRIPT', 'STYLE'].includes(tagName)) return document.createTextNode('');

  if (!ALLOWED_EDITOR_TAGS.has(tagName)) {
    const fragment = document.createDocumentFragment();
    Array.from(node.childNodes).forEach((child) => {
      fragment.appendChild(sanitizeEditorNode(child));
    });
    return fragment;
  }

  const element = document.createElement(tagName.toLowerCase());

  if (tagName === 'A') {
    const href = node.getAttribute('href') || '';
    if (isSafeEditorUrl(href)) {
      element.setAttribute('href', href);
      element.setAttribute('target', '_blank');
      element.setAttribute('rel', 'noreferrer');
    }
  }

  if (tagName === 'IMG') {
    const src = node.getAttribute('src') || '';
    if (!isSafeEditorUrl(src)) return document.createTextNode('');
    element.setAttribute('src', src);
    element.setAttribute('alt', node.getAttribute('alt') || '');
  }

  if (tagName === 'FIGURE') {
    element.setAttribute('data-block-type', 'image');
    const imageSize = node.getAttribute('data-image-size') || 'full';
    if (IMAGE_SIZE_OPTIONS.some((option) => option.id === imageSize)) {
      element.setAttribute('data-image-size', imageSize);
    }
  }

  Array.from(node.childNodes).forEach((child) => {
    element.appendChild(sanitizeEditorNode(child));
  });

  return element;
};

const sanitizeEditorHtml = (html = '') => {
  if (typeof document === 'undefined') return escapeHtml(html);

  const template = document.createElement('template');
  template.innerHTML = html;
  const fragment = document.createDocumentFragment();

  Array.from(template.content.childNodes).forEach((node) => {
    fragment.appendChild(sanitizeEditorNode(node));
  });

  const container = document.createElement('div');
  container.appendChild(fragment);

  return container.innerHTML;
};

const sanitizeEditorElement = (element) => {
  const wrapper = document.createElement('div');
  wrapper.appendChild(sanitizeEditorNode(element));
  return wrapper.innerHTML;
};

const textToParagraphHtml = (text = '') => (
  text
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
    .join('')
);

const parseEditorBlocks = (body = '') => {
  if (!body.trim()) return [];

  const blocks = body
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => {
      if (block.startsWith('![Image](') && block.endsWith(')')) {
        return { type: 'image', url: block.replace(/^!\[Image\]\(/, '').replace(/\)$/, '') };
      }
      if (/^<(figure|ul|ol|hr|pre|blockquote)\b/i.test(block)) return { type: 'html', html: block };
      if (block.startsWith('### ')) return { type: 'h3', text: block.replace(/^### /, '') };
      if (block.startsWith('## ')) return { type: 'h3', text: block.replace(/^## /, '') };
      if (block.startsWith('> ')) return { type: 'quote', text: block.replace(/^> /, '') };
      return { type: 'p', text: block };
    });

  if (blocks[blocks.length - 1]?.type === 'image') {
    return [...blocks, { type: 'p', text: '' }];
  }

  return blocks;
};

const serializeEditorBlocks = (blocks) => (
  blocks
    .map((block) => {
      if (block.type === 'image') return `![Image](${block.url})`;
      if (block.type === 'html') return block.html;
      if (block.type === 'h3') return block.text ? `## ${block.text}` : '';
      if (block.type === 'quote') return block.text ? `> ${block.text}` : '';
      return block.text || '';
    })
    .filter(Boolean)
    .join('\n\n')
);

const bodyToEditorHtml = (body = '') => (
  parseEditorBlocks(body)
    .map((block) => {
      if (block.type === 'image') return `<figure data-block-type="image" data-image-size="full"><img src="${escapeHtml(block.url)}" alt="" /></figure>`;
      if (block.type === 'html') return sanitizeEditorHtml(block.html);
      if (block.type === 'h3') return `<h2>${block.text ? sanitizeEditorHtml(block.text) : '<br>'}</h2>`;
      if (block.type === 'quote') return `<blockquote>${block.text ? sanitizeEditorHtml(block.text) : '<br>'}</blockquote>`;
      return `<p>${block.text ? sanitizeEditorHtml(block.text) : '<br>'}</p>`;
    })
    .join('')
);

const cleanEditorHtml = (html = '') => (
  sanitizeEditorHtml(html)
    .replace(/&nbsp;/g, ' ')
    .replace(/<div><br><\/div>/g, '')
    .trim()
);

const FieldLabel = ({ children }) => (
  <span className="mb-2 block text-[11px] font-medium text-zinc-500">{children}</span>
);

const StatusPill = ({ isPublished }) => (
  <span className="inline-flex items-center gap-1 text-sm text-zinc-600">
    <span className={`h-2 w-2 rounded-full ${isPublished ? 'bg-green-600' : 'bg-zinc-300'}`} />
    {isPublished ? 'Published' : 'Draft'}
  </span>
);

const LoginView = ({ error, isBusy, loginForm, setLoginForm, onLogin }) => (
  <div className="min-h-screen bg-white text-zinc-950">
    <div className="mx-auto flex min-h-screen max-w-sm flex-col justify-center px-5">
      <div className="mb-8 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-950 text-white">
          <Lock size={17} />
        </div>
        <div>
          <p className="text-sm font-semibold">Panometra Admin</p>
          <p className="text-sm text-zinc-500">Editorial access</p>
        </div>
      </div>

      <form onSubmit={onLogin} className="space-y-4">
        <label className="block">
          <FieldLabel>Email</FieldLabel>
          <input
            type="email"
            value={loginForm.email}
            onChange={(event) => setLoginForm({ ...loginForm, email: event.target.value })}
            className="w-full rounded-md border border-zinc-200 px-3 py-3 text-sm outline-none transition focus:border-zinc-950"
            required
          />
        </label>
        <label className="block">
          <FieldLabel>Password</FieldLabel>
          <input
            type="password"
            value={loginForm.password}
            onChange={(event) => setLoginForm({ ...loginForm, password: event.target.value })}
            className="w-full rounded-md border border-zinc-200 px-3 py-3 text-sm outline-none transition focus:border-zinc-950"
            required
          />
        </label>

        {error && (
          <div className="flex gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isBusy}
          className="w-full rounded-full bg-zinc-950 px-5 py-3 text-sm font-medium text-white transition hover:bg-zinc-800 disabled:opacity-50"
        >
          {isBusy ? 'Signing in...' : 'Sign in'}
        </button>
      </form>
    </div>
  </div>
);

const StoryThumb = ({ item }) => {
  if (item.imageUrl) {
    return (
      <div className="h-12 w-16 shrink-0 overflow-hidden bg-zinc-100">
        <img src={item.imageUrl} alt="" className="h-full w-full object-cover" />
      </div>
    );
  }

  return (
    <div className="flex h-12 w-16 shrink-0 items-center justify-center bg-zinc-100 text-zinc-400">
      <FileText size={17} />
    </div>
  );
};

const EmptyRows = ({ tab, onWrite }) => (
  <div className="border-t border-zinc-200 py-16 text-center">
    <h2 className="text-xl font-semibold">No {tab.toLowerCase()} stories</h2>
    <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-zinc-500">
      Your saved stories will appear here once there is something in this section.
    </p>
    <button
      type="button"
      onClick={onWrite}
      className="mt-6 inline-flex items-center gap-2 rounded-full border border-zinc-950 px-4 py-2 text-sm font-medium"
    >
      <Plus size={15} />
      Write a story
    </button>
  </div>
);

const PublishModal = ({
  error,
  form,
  isBusy,
  onClose,
  onDelete,
  onImageUpload,
  onPublish,
  readTime,
  slugPreview,
  updateForm,
  wordCount,
  isEditing,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 px-4 py-6 backdrop-blur-sm">
    <div className="max-h-[92vh] w-full max-w-3xl overflow-auto rounded-md border border-zinc-200 bg-white shadow-xl">
      <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold">Publish story</h2>
          <p className="mt-1 text-sm text-zinc-500">Review details before this appears on Intelligence.</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-zinc-100"
          title="Close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="grid gap-8 p-5 md:grid-cols-[minmax(0,1fr)_280px] md:p-6">
        <section>
          <p className="text-sm font-semibold">Story preview</p>
          <div className="mt-4 overflow-hidden rounded-md border border-zinc-200">
            {form.imageUrl ? (
              <img src={form.imageUrl} alt="" className="aspect-[16/9] w-full object-cover" />
            ) : (
              <div className="flex aspect-[16/9] items-center justify-center bg-zinc-100 text-sm text-zinc-500">
                No cover image
              </div>
            )}
            <div className="p-4">
              <h3 className="text-2xl font-serif font-semibold leading-tight">{form.title || 'Untitled story'}</h3>
              {form.subtitle && <p className="mt-2 text-sm leading-6 text-zinc-500">{form.subtitle}</p>}
              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                <span>{form.author || 'Panometra Research'}</span>
                <span>/</span>
                <span>{readTime}</span>
                <span>/</span>
                <span>{wordCount} words</span>
              </div>
            </div>
          </div>

          <label className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-zinc-300 px-4 py-4 text-sm font-medium text-zinc-700 transition hover:border-zinc-950 hover:bg-zinc-50">
            <Upload size={16} />
            {form.imageUrl ? 'Replace cover image' : 'Upload cover image'}
            <input
              type="file"
              accept="image/*"
              onChange={onImageUpload}
              className="hidden"
            />
          </label>
        </section>

        <section className="space-y-5">
          <div>
            <h3 className="text-sm font-semibold">Story settings</h3>
            <div className="mt-4 space-y-4">
              <label className="block">
                <FieldLabel>Author</FieldLabel>
                <input
                  value={form.author}
                  onChange={(event) => updateForm('author', event.target.value)}
                  className="w-full border-b border-zinc-200 py-2 text-sm outline-none focus:border-zinc-950"
                />
              </label>
              <label className="block">
                <FieldLabel>Slug</FieldLabel>
                <input
                  value={form.slug}
                  onChange={(event) => updateForm('slug', event.target.value)}
                  placeholder={slugPreview || 'auto-from-title'}
                  className="w-full border-b border-zinc-200 py-2 text-sm outline-none focus:border-zinc-950"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <FieldLabel>Category</FieldLabel>
                  <select
                    value={form.category}
                    onChange={(event) => updateForm('category', event.target.value)}
                    className="w-full border-b border-zinc-200 bg-white py-2 text-sm outline-none focus:border-zinc-950"
                  >
                    {CATEGORIES.map((category) => <option key={category}>{category}</option>)}
                  </select>
                </label>
                <label className="block">
                  <FieldLabel>Type</FieldLabel>
                  <select
                    value={form.mediaType}
                    onChange={(event) => updateForm('mediaType', event.target.value)}
                    className="w-full border-b border-zinc-200 bg-white py-2 text-sm capitalize outline-none focus:border-zinc-950"
                  >
                    {MEDIA_TYPES.map((type) => <option key={type}>{type}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <FieldLabel>Publish date</FieldLabel>
                <input
                  type="date"
                  value={form.publishedAt}
                  onChange={(event) => updateForm('publishedAt', event.target.value)}
                  className="w-full border-b border-zinc-200 py-2 text-sm outline-none focus:border-zinc-950"
                />
              </label>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold">Media links</h3>
            <div className="mt-4 space-y-4">
              <label className="block">
                <FieldLabel>Podcast/video URL</FieldLabel>
                <input
                  value={form.mediaUrl}
                  onChange={(event) => updateForm('mediaUrl', event.target.value)}
                  className="w-full border-b border-zinc-200 py-2 text-sm outline-none focus:border-zinc-950"
                />
              </label>
              <label className="block">
                <FieldLabel>External source URL</FieldLabel>
                <input
                  value={form.sourceUrl}
                  onChange={(event) => updateForm('sourceUrl', event.target.value)}
                  className="w-full border-b border-zinc-200 py-2 text-sm outline-none focus:border-zinc-950"
                />
              </label>
            </div>
          </div>

          <div className="space-y-3 text-sm text-zinc-500">
            <div className="flex items-center gap-2">
              <Clock size={14} />
              <span>{readTime}</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText size={14} />
              <span>{wordCount} words</span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <Link size={14} className="shrink-0" />
              <span className="truncate">{slugPreview ? `/intelligence/${slugPreview}` : 'Slug appears after save'}</span>
            </div>
          </div>

          {error && (
            <div className="flex gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <div className="flex flex-col gap-3 border-t border-zinc-100 pt-5">
            <button
              type="button"
              onClick={onPublish}
              disabled={isBusy}
              className="rounded-full bg-green-700 px-5 py-3 text-sm font-medium text-white transition hover:bg-green-800 disabled:opacity-50"
            >
              {isBusy ? 'Publishing...' : isEditing ? 'Update and publish' : 'Publish now'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-zinc-300 px-5 py-3 text-sm font-medium text-zinc-700 transition hover:border-zinc-950"
            >
              Keep editing
            </button>
            {isEditing && (
              <button
                type="button"
                onClick={onDelete}
                disabled={isBusy}
                className="inline-flex justify-center gap-2 text-sm text-red-700 disabled:opacity-50"
              >
                <Trash2 size={15} />
                Delete story
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  </div>
);

const LeaveEditorModal = ({ isBusy, isEditing, onCancel, onDelete, onSaveDraft }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 px-4 py-6 backdrop-blur-sm">
    <div className="w-full max-w-md rounded-md border border-zinc-200 bg-white p-5 shadow-xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Save this story?</h2>
          <p className="mt-2 text-sm leading-6 text-zinc-500">
            Keep it as a draft so you can finish it later, or delete it and return to Stories.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full hover:bg-zinc-100"
          title="Keep writing"
        >
          <X size={18} />
        </button>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={isBusy}
          className="rounded-full border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 transition hover:border-zinc-950 disabled:opacity-50"
        >
          Keep writing
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={isBusy}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-red-200 px-4 py-2 text-sm font-medium text-red-700 transition hover:border-red-700 disabled:opacity-50"
        >
          <Trash2 size={15} />
          {isEditing ? 'Delete story' : 'Delete draft'}
        </button>
        <button
          type="button"
          onClick={onSaveDraft}
          disabled={isBusy}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-zinc-950 px-4 py-2 text-sm font-medium text-white transition hover:bg-zinc-800 disabled:opacity-50"
        >
          <Save size={15} />
          {isBusy ? 'Saving...' : 'Save to drafts'}
        </button>
      </div>
    </div>
  </div>
);

export const AdminPage = () => {
  const [session, setSession] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [form, setForm] = useState(getFreshForm);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState('list');
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState('drafts');
  const [publishOpen, setPublishOpen] = useState(false);
  const [leavePromptOpen, setLeavePromptOpen] = useState(false);
  const [openBlockMenu, setOpenBlockMenu] = useState(null);
  const [paragraphMenu, setParagraphMenu] = useState(null);
  const [selectionToolbar, setSelectionToolbar] = useState(null);
  const [imageToolbar, setImageToolbar] = useState(null);
  const [slashMenu, setSlashMenu] = useState(null);
  const [editorVersion, setEditorVersion] = useState(0);
  const [autosaveStatus, setAutosaveStatus] = useState('Saved');
  const bodyEditorRef = useRef(null);
  const editorShellRef = useRef(null);
  const subtitleRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const selectedFigureRef = useRef(null);
  const inlineImageInputRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const latestBodyRef = useRef('');
  const bodyStateFrameRef = useRef(null);

  const isEditing = Boolean(form.databaseId);
  const readTime = form.readTime.trim() || estimateReadTime(form.body);
  const slugPreview = createSlugPreview(form.slug || form.title);
  const wordCount = countWords(form.body);

  const tabCounts = useMemo(() => ({
    drafts: items.filter((item) => !item.isPublished).length,
    scheduled: 0,
    published: items.filter((item) => item.isPublished).length,
    unlisted: 0,
    submissions: 0,
  }), [items]);

  const filteredItems = useMemo(() => {
    const search = query.trim().toLowerCase();

    return items.filter((item) => {
      const matchesTab =
        (activeTab === 'drafts' && !item.isPublished) ||
        (activeTab === 'published' && item.isPublished) ||
        activeTab === 'scheduled' ||
        activeTab === 'unlisted' ||
        activeTab === 'submissions';
      const hasContentForTab = activeTab === 'drafts' || activeTab === 'published';
      const haystack = `${item.title} ${item.subtitle} ${item.author} ${item.category}`.toLowerCase();

      return hasContentForTab && matchesTab && (!search || haystack.includes(search));
    });
  }, [activeTab, items, query]);

  const loadItems = async () => {
    setIsLoading(true);
    setError('');

    const result = await fetchAdminIntelligence();
    if (result.success) {
      setItems(result.data);
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    const loadSession = async () => {
      const result = await getAdminSession();
      if (result.session) {
        setSession(result.session);
        await loadItems();
      }
    };

    loadSession();
  }, []);

  useEffect(() => {
    if (mode !== 'editor' || !bodyEditorRef.current) return;

    latestBodyRef.current = form.body;
    bodyEditorRef.current.innerHTML = bodyToEditorHtml(form.body);

    try {
      document.execCommand('defaultParagraphSeparator', false, 'p');
    } catch {
      // Older browsers may ignore this; native contenteditable behavior still works.
    }
  }, [editorVersion, mode]);

  useEffect(() => () => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    if (bodyStateFrameRef.current) cancelAnimationFrame(bodyStateFrameRef.current);
  }, []);

  const updateForm = (key, value) => {
    const nextForm = { ...form, [key]: value };
    setForm(nextForm);
    scheduleLocalAutosave(nextForm);
  };

  const getDraftKey = (nextForm = form) => (
    `panometra_editor_draft_${nextForm.databaseId || 'new'}`
  );

  const scheduleLocalAutosave = (nextForm = form) => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    setAutosaveStatus('Unsaved changes');

    autosaveTimerRef.current = setTimeout(() => {
      try {
        const draft = {
          ...nextForm,
          body: mode === 'editor' ? latestBodyRef.current : nextForm.body,
          savedAt: new Date().toISOString(),
        };
        localStorage.setItem(getDraftKey(nextForm), JSON.stringify(draft));
        setAutosaveStatus('Saved locally');
      } catch {
        setAutosaveStatus('Autosave failed');
      }
    }, AUTOSAVE_DELAY_MS);
  };

  const clearLocalDraft = (nextForm = form) => {
    localStorage.removeItem(getDraftKey(nextForm));
  };

  const getLocalDraft = (nextForm) => {
    try {
      const localDraft = localStorage.getItem(getDraftKey(nextForm));
      return localDraft ? JSON.parse(localDraft) : null;
    } catch {
      clearLocalDraft(nextForm);
      return null;
    }
  };

  const startNew = () => {
    const freshForm = getFreshForm();
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = null;
    clearLocalDraft(freshForm);
    latestBodyRef.current = freshForm.body;
    setForm(freshForm);
    setEditorVersion((current) => current + 1);
    setError('');
    setStatus('');
    setAutosaveStatus('Saved');
    setPublishOpen(false);
    setLeavePromptOpen(false);
    setOpenBlockMenu(null);
    setParagraphMenu(null);
    setSelectionToolbar(null);
    setImageToolbar(null);
    setSlashMenu(null);
    selectedFigureRef.current = null;
    setMode('editor');
  };

  const editItem = (item) => {
    const nextForm = itemToFormData(item);
    const localDraft = getLocalDraft(nextForm);
    const recoveredForm = localDraft ? { ...nextForm, ...localDraft } : nextForm;
    latestBodyRef.current = recoveredForm.body;
    setForm(recoveredForm);
    setEditorVersion((current) => current + 1);
    setError('');
    setStatus('');
    setAutosaveStatus(localDraft ? 'Recovered draft' : 'Saved');
    setPublishOpen(false);
    setLeavePromptOpen(false);
    setOpenBlockMenu(null);
    setParagraphMenu(null);
    setSelectionToolbar(null);
    setImageToolbar(null);
    setSlashMenu(null);
    selectedFigureRef.current = null;
    setMode('editor');
  };

  const readImageFile = (event, onLoad) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      event.target.value = '';
      return;
    }

    if (file.size > MAX_INLINE_IMAGE_BYTES) {
      setError('Choose an image under 5 MB so the editor stays fast.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      onLoad(reader.result);
      setError('');
      event.target.value = '';
    };
    reader.onerror = () => {
      setError('Could not read that image. Try another file.');
      event.target.value = '';
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (event) => {
    readImageFile(event, (dataUrl) => {
      const nextForm = { ...form, imageUrl: dataUrl };
      setForm(nextForm);
      scheduleLocalAutosave(nextForm);
    });
  };

  const serializeEditorDom = () => {
    const editor = bodyEditorRef.current;
    if (!editor) return form.body;

    return Array.from(editor.childNodes)
      .map((node) => {
        if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim();
        if (node.nodeType !== Node.ELEMENT_NODE) return '';

        const element = node;
        const image = element.matches('figure') ? element.querySelector('img') : null;
        const html = cleanEditorHtml(element.innerHTML);
        const text = element.textContent.trim();

        if (element.matches('figure') && image?.src) return sanitizeEditorElement(element);
        if (image?.src) return `![Image](${image.src})`;
        if (element.matches('hr')) return '<hr>';
        if (element.matches('ul,ol,pre')) return sanitizeEditorElement(element);
        if (!html || html === '<br>' || !text) return '';
        if (element.matches('h1,h2,h3')) return `## ${html}`;
        if (element.matches('blockquote')) return `> ${html}`;
        return html;
      })
      .filter(Boolean)
      .join('\n\n');
  };

  const syncEditorBody = (nextBody = serializeEditorDom(), options = {}) => {
    latestBodyRef.current = nextBody;

    const commitBody = () => {
      setForm((current) => (
        current.body === latestBodyRef.current
          ? current
          : { ...current, body: latestBodyRef.current }
      ));
    };

    if (options.immediate) {
      if (bodyStateFrameRef.current) {
        cancelAnimationFrame(bodyStateFrameRef.current);
        bodyStateFrameRef.current = null;
      }
      commitBody();
      return nextBody;
    }

    if (!bodyStateFrameRef.current) {
      bodyStateFrameRef.current = requestAnimationFrame(() => {
        bodyStateFrameRef.current = null;
        commitBody();
      });
    }

    return nextBody;
  };

  const getCurrentEditorForm = () => {
    const body = mode === 'editor' ? serializeEditorDom() : form.body;
    latestBodyRef.current = body;

    return {
      ...form,
      body,
    };
  };

  const hasDraftableContent = (nextForm) => (
    Boolean(
      nextForm.title.trim() ||
      nextForm.slug.trim() ||
      nextForm.subtitle.trim() ||
      nextForm.body.trim() ||
      nextForm.imageUrl.trim() ||
      nextForm.mediaUrl.trim() ||
      nextForm.sourceUrl.trim()
    )
  );

  const exitEditorToList = () => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = null;
    setPublishOpen(false);
    setLeavePromptOpen(false);
    setOpenBlockMenu(null);
    setParagraphMenu(null);
    setSelectionToolbar(null);
    setImageToolbar(null);
    setSlashMenu(null);
    selectedFigureRef.current = null;
    setStatus('');
    setError('');
    setMode('list');
  };

  const requestBackToStories = () => {
    const currentForm = getCurrentEditorForm();
    setForm(currentForm);

    if (hasDraftableContent(currentForm)) {
      setLeavePromptOpen(true);
      return;
    }

    clearLocalDraft(currentForm);
    exitEditorToList();
  };

  const saveSelection = () => {
    const editor = bodyEditorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) {
      setSelectionToolbar(null);
      setParagraphMenu(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const isInsideEditor = editor.contains(range.commonAncestorContainer);
    if (!isInsideEditor) {
      setSelectionToolbar(null);
      setParagraphMenu(null);
      return;
    }

    savedSelectionRef.current = range.cloneRange();
    if (selection.isCollapsed) {
      setSelectionToolbar(null);
      updateParagraphMenu();
      return;
    }

    const rect = range.getBoundingClientRect();
    const toolbarTop = rect.top > 70 ? rect.top - 58 : rect.bottom + 14;
    const toolbarWidth = 300;
    const toolbarLeft = Math.min(
      window.innerWidth - 24,
      Math.max(24 + (toolbarWidth / 2), rect.left + (rect.width / 2))
    );

    setParagraphMenu(null);
    setOpenBlockMenu(null);
    setSelectionToolbar(!selection.isCollapsed ? {
      visible: true,
      top: toolbarTop,
      left: toolbarLeft,
    } : null);
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (!selection || !savedSelectionRef.current) return;

    selection.removeAllRanges();
    selection.addRange(savedSelectionRef.current);
  };

  const runEditorCommand = (command, value = null) => {
    bodyEditorRef.current?.focus();
    restoreSelection();
    document.execCommand(command, false, value);
    const body = syncEditorBody(undefined, { immediate: true });
    scheduleLocalAutosave({ ...form, body });
    saveSelection();
  };

  const applyToolbarAction = (action) => {
    if (action === 'bold') runEditorCommand('bold');
    if (action === 'italic') runEditorCommand('italic');
    if (action === 'link') {
      const url = window.prompt('Paste a link');
      if (url) runEditorCommand('createLink', url);
    }
    if (action === 'heading') runEditorCommand('formatBlock', 'h2');
    if (action === 'paragraph') runEditorCommand('formatBlock', 'p');
    if (action === 'quote') runEditorCommand('formatBlock', 'blockquote');
  };

  const insertInlineImage = (dataUrl) => {
    bodyEditorRef.current?.focus();
    restoreSelection();
    document.execCommand(
      'insertHTML',
      false,
      `<figure data-block-type="image" data-image-size="full"><img src="${dataUrl}" alt="" /></figure><p><br></p>`
    );
    setOpenBlockMenu(null);
    const body = syncEditorBody(undefined, { immediate: true });
    scheduleLocalAutosave({ ...form, body });
  };

  const readImageBlob = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > MAX_INLINE_IMAGE_BYTES) {
      setError('Choose an image under 5 MB so the editor stays fast.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => insertInlineImage(reader.result);
    reader.onerror = () => setError('Could not read that image. Try another file.');
    reader.readAsDataURL(file);
  };

  const handleInlineImageUpload = (event) => {
    readImageFile(event, insertInlineImage);
  };

  const handleEditorPaste = (event) => {
    const image = Array.from(event.clipboardData?.items || [])
      .find((item) => item.type.startsWith('image/'));

    if (image) {
      event.preventDefault();
      readImageBlob(image.getAsFile());
      return;
    }

    const html = event.clipboardData?.getData('text/html');
    const text = event.clipboardData?.getData('text/plain');
    const htmlToInsert = html ? sanitizeEditorHtml(html) : textToParagraphHtml(text);

    if (!htmlToInsert) return;

    event.preventDefault();
    bodyEditorRef.current?.focus();
    document.execCommand('insertHTML', false, htmlToInsert);
    const body = syncEditorBody(undefined, { immediate: true });
    scheduleLocalAutosave({ ...form, body });
  };

  const handleEditorDrop = (event) => {
    const image = Array.from(event.dataTransfer?.files || [])
      .find((file) => file.type.startsWith('image/'));

    if (!image) return;

    event.preventDefault();
    bodyEditorRef.current?.focus();
    readImageBlob(image);
  };

  const insertEditorHtml = (html) => {
    bodyEditorRef.current?.focus();
    restoreSelection();
    document.execCommand('insertHTML', false, html);
    setOpenBlockMenu(null);
    setSlashMenu(null);
    const body = syncEditorBody(undefined, { immediate: true });
    scheduleLocalAutosave({ ...form, body });
  };

  const runBlockCommand = (command) => {
    if (command === 'image') {
      inlineImageInputRef.current?.click();
      return;
    }

    if (command === 'h2') insertEditorHtml('<h2><br></h2>');
    if (command === 'quote') insertEditorHtml('<blockquote><br></blockquote>');
    if (command === 'divider') insertEditorHtml('<hr><p><br></p>');
    if (command === 'code') insertEditorHtml('<pre><code><br></code></pre><p><br></p>');
    if (command === 'callout') insertEditorHtml('<blockquote><strong>Note:</strong> </blockquote><p><br></p>');
    if (command === 'bullets') insertEditorHtml('<ul><li><br></li></ul>');
    if (command === 'numbered') insertEditorHtml('<ol><li><br></li></ol>');
  };

  const runSlashCommand = (command) => {
    restoreSelection();
    const block = getCurrentEditableBlock();
    if (block && block.textContent?.trim().startsWith('/')) {
      block.innerHTML = '<br>';
    }
    runBlockCommand(command);
  };

  const getCurrentEditableBlock = () => {
    const editor = bodyEditorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return null;

    let node = selection.anchorNode;
    if (node?.nodeType === Node.TEXT_NODE) node = node.parentElement;
    if (!node || !editor.contains(node)) return null;

    return node.closest('p,h1,h2,h3,blockquote,li,pre') || editor;
  };

  const clearImageSelection = () => {
    selectedFigureRef.current?.removeAttribute('data-selected');
    selectedFigureRef.current = null;
    setImageToolbar(null);
  };

  const getFigureSize = (figure) => (
    figure?.getAttribute('data-image-size') || 'full'
  );

  const positionImageToolbar = (figure, overrides = {}) => {
    if (!figure) return;

    selectedFigureRef.current?.removeAttribute('data-selected');
    selectedFigureRef.current = figure;
    figure.setAttribute('data-selected', 'true');

    const rect = figure.getBoundingClientRect();
    const toolbarWidth = 330;
    const top = rect.top > 78 ? rect.top - 64 : rect.bottom + 14;
    const left = Math.min(
      window.innerWidth - 24,
      Math.max(24 + (toolbarWidth / 2), rect.left + (rect.width / 2))
    );

    setSelectionToolbar(null);
    setParagraphMenu(null);
    setOpenBlockMenu(null);
    setImageToolbar({
      visible: true,
      top,
      left,
      size: getFigureSize(figure),
      altOpen: false,
      altText: figure.querySelector('img')?.getAttribute('alt') || '',
      ...overrides,
    });
  };

  const updateParagraphMenu = (preferredBlock = null) => {
    const editor = bodyEditorRef.current;
    const shell = editorShellRef.current;
    const selection = window.getSelection();
    if (!editor || !shell || !selection || selection.rangeCount === 0 || !selection.isCollapsed) {
      setParagraphMenu(null);
      return;
    }

    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) {
      setParagraphMenu(null);
      return;
    }

    const block = preferredBlock || getCurrentEditableBlock();
    const isEmptyEditor = block === editor && !editor.textContent.trim() && editor.children.length === 0;
    const isEmptyParagraph = block?.matches?.('p,div') && !block.textContent.trim() && !block.querySelector('img');

    if (!isEmptyEditor && !isEmptyParagraph) {
      setParagraphMenu(null);
      if (openBlockMenu === 'paragraph') setOpenBlockMenu(null);
      return;
    }

    const blockRect = isEmptyEditor ? editor.getBoundingClientRect() : block.getBoundingClientRect();
    const shellRect = shell.getBoundingClientRect();
    setParagraphMenu({
      visible: true,
      top: Math.max(0, blockRect.top - shellRect.top + 2),
      left: -46,
    });
  };

  const handleEditorClick = (event) => {
    const editor = bodyEditorRef.current;
    if (!editor) return;

    const figure = event.target.closest?.('figure[data-block-type="image"], figure');
    if (figure && editor.contains(figure)) {
      positionImageToolbar(figure);
      return;
    }

    clearImageSelection();
    updateParagraphMenu();
  };

  const applyImageSize = (size) => {
    const figure = selectedFigureRef.current;
    if (!figure || !IMAGE_SIZE_OPTIONS.some((option) => option.id === size)) return;

    figure.setAttribute('data-block-type', 'image');
    figure.setAttribute('data-image-size', size);
    const body = syncEditorBody(undefined, { immediate: true });
    scheduleLocalAutosave({ ...form, body });
    positionImageToolbar(figure);
  };

  const editSelectedImageAlt = () => {
    const figure = selectedFigureRef.current;
    const image = figure?.querySelector('img');
    if (!figure || !image) return;

    setImageToolbar((current) => ({
      ...(current || {}),
      visible: true,
      altOpen: !current?.altOpen,
      altText: image.getAttribute('alt') || '',
    }));
  };

  const updateImageAltDraft = (altText) => {
    setImageToolbar((current) => ({
      ...(current || {}),
      altOpen: true,
      altText,
    }));
  };

  const commitSelectedImageAlt = () => {
    const figure = selectedFigureRef.current;
    const image = figure?.querySelector('img');
    if (!figure || !image) return;

    const altText = imageToolbar?.altText || '';
    image.setAttribute('alt', altText.trim());
    const body = syncEditorBody(undefined, { immediate: true });
    scheduleLocalAutosave({ ...form, body });
    positionImageToolbar(figure, { altOpen: false, altText: altText.trim() });
  };

  const moveCaretInto = (element) => {
    const selection = window.getSelection();
    if (!selection) return;

    const range = document.createRange();
    range.setStart(element, 0);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    savedSelectionRef.current = range.cloneRange();
  };

  const insertParagraphAfterCurrentBlock = () => {
    const editor = bodyEditorRef.current;
    const block = getCurrentEditableBlock();
    if (!editor || !block || block.matches?.('li,pre')) return false;

    const paragraph = document.createElement('p');
    paragraph.appendChild(document.createElement('br'));

    if (block === editor) {
      editor.appendChild(paragraph);
    } else {
      block.insertAdjacentElement('afterend', paragraph);
    }

    moveCaretInto(paragraph);
    const body = syncEditorBody(undefined, { immediate: true });
    scheduleLocalAutosave({ ...form, body });
    updateParagraphMenu(paragraph);
    return true;
  };

  const handleEditorKeyDown = (event) => {
    const isModifier = event.metaKey || event.ctrlKey;

    if (isModifier && event.key.toLowerCase() === 's') {
      event.preventDefault();
      handleSave(event);
      return;
    }

    if (isModifier && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      applyToolbarAction('link');
      return;
    }

    if (event.key === 'Escape') {
      setOpenBlockMenu(null);
      setSlashMenu(null);
      setSelectionToolbar(null);
      setParagraphMenu(null);
      clearImageSelection();
      return;
    }

    if (event.key === ' ' && !event.shiftKey) {
      const block = getCurrentEditableBlock();
      const text = block?.textContent?.trim();

      if (text === '#') {
        event.preventDefault();
        block.textContent = '';
        runEditorCommand('formatBlock', 'h1');
        return;
      }
      if (text === '##') {
        event.preventDefault();
        block.textContent = '';
        runEditorCommand('formatBlock', 'h2');
        return;
      }
      if (text === '###') {
        event.preventDefault();
        block.textContent = '';
        runEditorCommand('formatBlock', 'h3');
        return;
      }
      if (text === '>') {
        event.preventDefault();
        block.textContent = '';
        runEditorCommand('formatBlock', 'blockquote');
        return;
      }
      if (text === '-') {
        event.preventDefault();
        block.textContent = '';
        runEditorCommand('insertUnorderedList');
        setParagraphMenu(null);
        return;
      }
      if (text === '1.') {
        event.preventDefault();
        block.textContent = '';
        runEditorCommand('insertOrderedList');
        setParagraphMenu(null);
        return;
      }
    }

    if (event.key !== 'Enter' || event.shiftKey) return;

    const block = getCurrentEditableBlock();
    if (block?.textContent?.trim() === '---') {
      event.preventDefault();
      block.remove();
      insertEditorHtml('<hr><p><br></p>');
      return;
    }

    if (insertParagraphAfterCurrentBlock()) {
      event.preventDefault();
    }
    setSlashMenu(null);
  };

  const handleEditorInput = () => {
    const selection = window.getSelection();
    if (selection?.rangeCount) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    }

    const body = syncEditorBody();
    scheduleLocalAutosave({ ...form, body });

    const block = getCurrentEditableBlock();
    const text = block?.textContent || '';
    updateParagraphMenu(block);
    if (text.startsWith('/')) {
      const rect = block.getBoundingClientRect();
      setSlashMenu({
        query: text.slice(1).toLowerCase(),
        top: rect.bottom + 8,
        left: Math.max(24, rect.left),
      });
    } else {
      setSlashMenu(null);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsBusy(true);
    setError('');

    const result = await signInAdmin(loginForm);
    if (result.success) {
      setSession(result.data.session);
      await loadItems();
    } else {
      setError(result.error);
    }

    setIsBusy(false);
  };

  const handleSignOut = async () => {
    await signOutAdmin();
    setSession(null);
    setItems([]);
    setStatus('');
    setError('');
  };

  const persistStory = async (nextForm, successMessage) => {
    const body = mode === 'editor' ? syncEditorBody(undefined, { immediate: true }) : nextForm.body;
    let formToSave = { ...nextForm, body };
    const isPublishing = Boolean(formToSave.isPublished);

    setIsBusy(true);
    setError('');
    setStatus('');

    if (!formToSave.title.trim()) {
      if (isPublishing) {
        setError('Add a title before publishing.');
        setIsBusy(false);
        return false;
      }

      formToSave = { ...formToSave, title: 'Untitled story' };
    }

    if (isPublishing && !formToSave.body.trim()) {
      setError('Add story text before publishing.');
      setIsBusy(false);
      return false;
    }

    const result = formToSave.databaseId
      ? await updateIntelligenceItem(formToSave.databaseId, formToSave)
      : await createIntelligenceItem(formToSave);

    if (result.success) {
      const savedForm = itemToFormData(result.data);
      setStatus(successMessage);
      setForm(savedForm);
      setEditorVersion((current) => current + 1);
      clearLocalDraft(formToSave);
      setAutosaveStatus('Saved');
      await loadItems();
      setIsBusy(false);
      return true;
    } else {
      setError(result.error);
    }

    setIsBusy(false);
    return false;
  };

  const handleSave = async (event) => {
    event?.preventDefault();
    await persistStory(form, form.isPublished ? 'Updated' : 'Saved');
  };

  const handlePublish = async () => {
    const nextForm = { ...form, isPublished: true };
    setForm(nextForm);
    const saved = await persistStory(nextForm, 'Published');
    if (saved) {
      setPublishOpen(false);
    }
  };

  const handleSaveDraftAndLeave = async () => {
    const currentForm = getCurrentEditorForm();
    const draftForm = { ...currentForm, isPublished: false };
    setForm(draftForm);

    const saved = await persistStory(draftForm, 'Draft saved');
    if (saved) {
      exitEditorToList();
    } else {
      setLeavePromptOpen(false);
    }
  };

  const handleDeleteAndLeave = async () => {
    const currentForm = getCurrentEditorForm();
    setIsBusy(true);
    setError('');
    setStatus('');

    if (currentForm.databaseId) {
      const result = await deleteIntelligenceItem(currentForm.databaseId);
      if (!result.success) {
        setError(result.error);
        setLeavePromptOpen(false);
        setIsBusy(false);
        return;
      }

      await loadItems();
    }

    const freshForm = getFreshForm();
    clearLocalDraft(currentForm);
    clearLocalDraft(freshForm);
    latestBodyRef.current = freshForm.body;
    setForm(freshForm);
    setEditorVersion((current) => current + 1);
    setIsBusy(false);
    exitEditorToList();
  };

  const handleDelete = async () => {
    if (!form.databaseId) return;
    const shouldDelete = window.confirm('Delete this story? This cannot be undone.');
    if (!shouldDelete) return;

    setIsBusy(true);
    setError('');
    setStatus('');

    const result = await deleteIntelligenceItem(form.databaseId);
    if (result.success) {
      const freshForm = getFreshForm();
      latestBodyRef.current = freshForm.body;
      setForm(freshForm);
      setEditorVersion((current) => current + 1);
      setMode('list');
      await loadItems();
    } else {
      setError(result.error);
    }

    setIsBusy(false);
  };

  const handleDeleteItem = async (item) => {
    const shouldDelete = window.confirm(`Delete "${item.title || 'Untitled story'}"? This cannot be undone.`);
    if (!shouldDelete) return;

    setIsBusy(true);
    setError('');

    const result = await deleteIntelligenceItem(item.databaseId);
    if (result.success) {
      if (form.databaseId === item.databaseId) {
        const freshForm = getFreshForm();
        latestBodyRef.current = freshForm.body;
        setForm(freshForm);
      }
      await loadItems();
    } else {
      setError(result.error);
    }

    setIsBusy(false);
  };

  if (!session) {
    return (
      <LoginView
        error={error}
        isBusy={isBusy}
        loginForm={loginForm}
        setLoginForm={setLoginForm}
        onLogin={handleLogin}
      />
    );
  }

  if (mode === 'editor') {
    return (
      <div className="min-h-screen bg-white text-zinc-950">
        <header className="sticky top-0 z-20 border-b border-zinc-100 bg-white/95 backdrop-blur">
          <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={requestBackToStories}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-zinc-100"
                title="Back to stories"
              >
                <ArrowLeft size={18} />
              </button>
              <span className="hidden text-sm text-zinc-500 sm:inline">Stories</span>
            </div>

            <div className="flex items-center gap-3">
              {status && (
                <span className="hidden items-center gap-1 text-sm text-green-700 sm:inline-flex">
                  <Check size={15} />
                  {status}
                </span>
              )}
              {!status && (
                <span className="hidden text-sm text-zinc-500 sm:inline">
                  {autosaveStatus}
                </span>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={isBusy}
                className="hidden rounded-full border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 disabled:opacity-50 sm:inline-flex"
              >
                {isBusy ? 'Saving...' : 'Save draft'}
              </button>
              <button
                type="button"
                onClick={() => setPublishOpen(true)}
                disabled={isBusy}
                className="rounded-full bg-green-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-800 disabled:opacity-50"
              >
                Publish
              </button>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-[740px] px-5 py-10">
          <form onSubmit={handleSave}>
            {error && (
              <div className="mb-6 flex gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <p>{error}</p>
              </div>
            )}

            <input
              value={form.title}
              onChange={(event) => {
                const nextForm = { ...form, title: event.target.value };
                setForm(nextForm);
                scheduleLocalAutosave(nextForm);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  subtitleRef.current?.focus();
                }
              }}
              placeholder="Title"
              className="w-full bg-transparent text-5xl font-serif leading-tight outline-none placeholder:text-zinc-300 md:text-6xl"
              required
            />
            <textarea
              ref={subtitleRef}
              value={form.subtitle}
              onChange={(event) => {
                const nextForm = { ...form, subtitle: event.target.value };
                setForm(nextForm);
                scheduleLocalAutosave(nextForm);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  bodyEditorRef.current?.focus();
                }
              }}
              placeholder="Tell readers what this story is about"
              rows={2}
              className="mt-4 w-full resize-none bg-transparent text-xl leading-8 text-zinc-500 outline-none placeholder:text-zinc-300"
            />
            {form.imageUrl ? (
              <div className="group relative mt-8 overflow-hidden bg-zinc-100">
                <img src={form.imageUrl} alt="" className="max-h-[440px] w-full object-cover" />
                <label className="absolute right-4 top-4 cursor-pointer rounded-full bg-white/95 px-4 py-2 text-sm font-medium shadow-sm transition hover:bg-white">
                  Replace image
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
              </div>
            ) : (
              <label className="mt-8 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-zinc-300 px-5 py-10 text-sm font-medium text-zinc-500 transition hover:border-zinc-950 hover:text-zinc-950">
                <Upload size={17} />
                Add cover image
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
            )}
            <div ref={editorShellRef} className="relative mt-8">
              {paragraphMenu?.visible && (
                <button
                  type="button"
                  style={{ top: paragraphMenu.top, left: paragraphMenu.left }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    bodyEditorRef.current?.focus();
                    saveSelection();
                    clearImageSelection();
                    setOpenBlockMenu(openBlockMenu === 'paragraph' ? null : 'paragraph');
                  }}
                  className={`absolute z-20 inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white transition ${
                    openBlockMenu === 'paragraph'
                      ? 'rotate-45 border-zinc-950 text-zinc-950'
                      : 'border-zinc-400 text-zinc-500'
                  }`}
                  title={openBlockMenu === 'paragraph' ? 'Close menu' : 'Open insert menu'}
                >
                  <Plus size={18} />
                </button>
              )}

              {paragraphMenu?.visible && openBlockMenu === 'paragraph' && (
                <div
                  className="absolute z-30 w-64 rounded-md border border-zinc-200 bg-white p-2 shadow-xl"
                  style={{ top: paragraphMenu.top, left: 0 }}
                >
                  {BLOCK_COMMANDS.map((command) => {
                    const Icon = command.icon;

                    return (
                      <button
                        key={command.id}
                        type="button"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => runBlockCommand(command.id)}
                        className="flex w-full items-center gap-3 rounded px-3 py-2 text-left hover:bg-zinc-50"
                      >
                        <span className="flex h-8 w-8 items-center justify-center rounded border border-zinc-200 text-zinc-500">
                          <Icon size={15} />
                        </span>
                        <span>
                          <span className="block text-sm font-medium">{command.label}</span>
                          <span className="block text-xs text-zinc-500">{command.description}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {selectionToolbar?.visible && (
                <div
                  className="fixed z-40 flex -translate-x-1/2 items-center rounded-md bg-zinc-900 px-3 py-2 text-white shadow-xl after:absolute after:left-1/2 after:top-full after:-translate-x-1/2 after:border-8 after:border-transparent after:border-t-zinc-900"
                  style={{ top: selectionToolbar.top, left: selectionToolbar.left }}
                  onMouseDown={(event) => event.preventDefault()}
                >
                  <button type="button" onClick={() => applyToolbarAction('bold')} className="px-2 text-xl font-bold">B</button>
                  <button type="button" onClick={() => applyToolbarAction('italic')} className="px-2 text-xl italic">i</button>
                  <button type="button" onClick={() => applyToolbarAction('link')} className="px-2"><Link size={18} /></button>
                  <span className="mx-2 h-6 w-px bg-white/25" />
                  <button type="button" onClick={() => applyToolbarAction('heading')} className="px-2 text-xl font-bold">T</button>
                  <button type="button" onClick={() => applyToolbarAction('paragraph')} className="px-2 text-base font-bold">T</button>
                  <button type="button" onClick={() => applyToolbarAction('quote')} className="px-2 text-2xl leading-none">“</button>
                </div>
              )}

              {imageToolbar?.visible && (
                <div
                  className="fixed z-40 flex -translate-x-1/2 items-center gap-1 rounded-md bg-zinc-900 px-3 py-2 text-white shadow-xl after:absolute after:left-1/2 after:top-full after:-translate-x-1/2 after:border-8 after:border-transparent after:border-t-zinc-900"
                  style={{ top: imageToolbar.top, left: imageToolbar.left }}
                  onMouseDown={(event) => {
                    if (event.target.tagName !== 'INPUT') event.preventDefault();
                  }}
                >
                  {IMAGE_SIZE_OPTIONS.map((option) => {
                    const Icon = option.icon;

                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => applyImageSize(option.id)}
                        className={`inline-flex h-9 w-10 items-center justify-center rounded transition ${
                          imageToolbar.size === option.id
                            ? 'bg-white/15 text-green-300'
                            : 'text-white hover:bg-white/10'
                        }`}
                        title={option.label}
                      >
                        <Icon size={20} />
                      </button>
                    );
                  })}
                  <span className="mx-2 h-7 w-px bg-white/25" />
                  {imageToolbar.altOpen ? (
                    <div className="flex items-center gap-2">
                      <input
                        value={imageToolbar.altText || ''}
                        onChange={(event) => updateImageAltDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            commitSelectedImageAlt();
                          }
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            positionImageToolbar(selectedFigureRef.current);
                          }
                        }}
                        aria-label="Image alt text"
                        className="h-8 w-44 rounded border border-white/20 bg-zinc-800 px-2 text-sm text-white outline-none placeholder:text-zinc-400 focus:border-white/60"
                        placeholder="Alt text"
                      />
                      <button
                        type="button"
                        onClick={commitSelectedImageAlt}
                        className="rounded px-3 py-2 text-sm font-medium text-white transition hover:bg-white/10"
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={editSelectedImageAlt}
                      className="rounded px-3 py-2 text-sm font-medium text-white transition hover:bg-white/10"
                    >
                      Alt text
                    </button>
                  )}
                </div>
              )}

              <div
                ref={bodyEditorRef}
                contentEditable
                suppressContentEditableWarning
                onClick={handleEditorClick}
                onInput={handleEditorInput}
                onKeyDown={handleEditorKeyDown}
                onPaste={handleEditorPaste}
                onDrop={handleEditorDrop}
                onDragOver={(event) => event.preventDefault()}
                onMouseUp={saveSelection}
                onKeyUp={saveSelection}
                onBlur={() => syncEditorBody(undefined, { immediate: true })}
                data-placeholder="Tell your story..."
                className="admin-rich-editor min-h-[420px] w-full outline-none"
              />

              {slashMenu && (
                <div
                  className="fixed z-30 w-64 rounded-md border border-zinc-200 bg-white p-2 shadow-xl"
                  style={{ top: slashMenu.top, left: slashMenu.left }}
                >
                  {BLOCK_COMMANDS
                    .filter((command) => command.label.toLowerCase().includes(slashMenu.query) || command.id.includes(slashMenu.query))
                    .map((command) => {
                      const Icon = command.icon;

                      return (
                        <button
                          key={command.id}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            runSlashCommand(command.id);
                          }}
                          className="flex w-full items-center gap-3 rounded px-3 py-2 text-left hover:bg-zinc-50"
                        >
                          <span className="flex h-8 w-8 items-center justify-center rounded border border-zinc-200 text-zinc-500">
                            <Icon size={15} />
                          </span>
                          <span>
                            <span className="block text-sm font-medium">{command.label}</span>
                            <span className="block text-xs text-zinc-500">{command.description}</span>
                          </span>
                        </button>
                      );
                    })}
                </div>
              )}

              <input
                ref={inlineImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleInlineImageUpload}
                className="hidden"
              />
            </div>
          </form>
        </div>

        {publishOpen && (
          <PublishModal
            error={error}
            form={form}
            isBusy={isBusy}
            isEditing={isEditing}
            onClose={() => setPublishOpen(false)}
            onDelete={handleDelete}
            onImageUpload={handleImageUpload}
            onPublish={handlePublish}
            readTime={readTime}
            slugPreview={slugPreview}
            updateForm={updateForm}
            wordCount={wordCount}
          />
        )}

        {leavePromptOpen && (
          <LeaveEditorModal
            isBusy={isBusy}
            isEditing={isEditing}
            onCancel={() => setLeavePromptOpen(false)}
            onDelete={handleDeleteAndLeave}
            onSaveDraft={handleSaveDraftAndLeave}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-zinc-950">
      <header className="border-b border-zinc-100">
        <div className="mx-auto flex h-12 max-w-6xl items-center justify-between px-4">
          <div className="relative hidden w-64 sm:block">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search"
              className="w-full rounded-full bg-zinc-100 py-2 pl-9 pr-3 text-sm outline-none"
            />
          </div>
          <div className="sm:hidden">
            <p className="text-sm font-semibold">Panometra</p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <button
              type="button"
              onClick={startNew}
              aria-label="Create a new story"
              className="inline-flex items-center gap-2 text-zinc-700 hover:text-zinc-950"
            >
              <Edit3 size={16} />
              Write
            </button>
            <button
              type="button"
              onClick={loadItems}
              disabled={isLoading}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-zinc-100 disabled:opacity-50"
              title="Refresh stories"
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-zinc-100"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-5 py-12">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-4xl font-semibold tracking-tight">Stories</h1>
          <button
            type="button"
            onClick={startNew}
            className="rounded-full border border-zinc-950 px-4 py-2 text-sm font-medium hover:bg-zinc-50"
          >
            Write a story
          </button>
        </div>

        <div className="relative mt-6 sm:hidden">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search"
            className="w-full rounded-full bg-zinc-100 py-2 pl-9 pr-3 text-sm outline-none"
          />
        </div>

        <div className="mt-8 flex gap-6 overflow-x-auto border-b border-zinc-200 text-sm text-zinc-500">
          {STORY_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`shrink-0 border-b pb-3 ${
                tab.separated ? 'ml-2 border-l border-l-zinc-200 pl-6' : ''
              } ${
                activeTab === tab.id
                  ? 'border-b-zinc-950 text-zinc-950'
                  : 'border-b-transparent hover:text-zinc-950'
              }`}
            >
              {tab.label} {tabCounts[tab.id] ? <span className="ml-1">{tabCounts[tab.id]}</span> : null}
            </button>
          ))}
        </div>

        {error && (
          <div className="mt-6 flex gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="mt-8 hidden grid-cols-[minmax(0,1fr)_140px_120px_36px] gap-4 text-sm text-zinc-500 md:grid">
          <span>Latest</span>
          <span>Publication</span>
          <span>Status</span>
          <span />
        </div>

        <div className="mt-3">
          {filteredItems.length === 0 ? (
            <EmptyRows tab={STORY_TABS.find((tab) => tab.id === activeTab)?.label || 'Stories'} onWrite={startNew} />
          ) : (
            filteredItems.map((item) => (
              <div
                key={item.databaseId}
                className="grid grid-cols-[minmax(0,1fr)_36px] gap-4 border-t border-zinc-200 py-6 md:grid-cols-[minmax(0,1fr)_140px_120px_36px]"
              >
                <button type="button" onClick={() => editItem(item)} className="min-w-0 text-left">
                  <div className="flex min-w-0 gap-4">
                    <StoryThumb item={item} />
                    <div className="min-w-0">
                      <h2 className="truncate text-base font-semibold">{item.title || 'Untitled story'}</h2>
                      <p className="mt-2 line-clamp-2 text-sm leading-5 text-zinc-500">
                        {item.readTime || estimateReadTime('')} ({countWords(itemToFormData(item).body)} words) · {formatUpdatedDate(item)}
                      </p>
                      {item.subtitle && <p className="mt-1 line-clamp-1 text-sm text-zinc-500">{item.subtitle}</p>}
                    </div>
                  </div>
                </button>

                <div className="hidden items-center text-sm text-zinc-500 md:flex">
                  Panometra
                </div>
                <div className="hidden items-center md:flex">
                  <StatusPill isPublished={item.isPublished} />
                </div>
                <button
                  type="button"
                  onClick={() => activeTab === 'published' ? handleDeleteItem(item) : editItem(item)}
                  disabled={isBusy}
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 disabled:opacity-50 ${
                    activeTab === 'published' ? 'hover:text-red-700' : 'hover:text-zinc-950'
                  }`}
                  title={activeTab === 'published' ? 'Delete story' : 'Edit story'}
                >
                  {activeTab === 'published' ? <Trash2 size={17} /> : <MoreHorizontal size={17} />}
                </button>
              </div>
            ))
          )}
        </div>

      </div>
    </div>
  );
};
